import {
  Column,
  ColumnApi,
  ColumnGroup,
  GridApi,
  IRowNode,
} from "ag-grid-community";
import { createAndOpenPdf } from "lib/pdf";
import {
  formatDateString,
  formatLocalTimestampToDateTimeString,
} from "lib/timeFormats";
import greyscaleLogo from "img/AgriNous-logo-black-white-base64";

export const PdfOrientation = {
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
} as const;

type PdfOrientationValue = (typeof PdfOrientation)[keyof typeof PdfOrientation];

interface ExportAgGridToPdfOptions {
  orientation?: PdfOrientationValue;
  saleTitle?: string | null;
}

// Lightweight tree used to build multi-row group headers.
interface ColNode {
  label: string;
  children: ColNode[];
}

function getCellValue(
  gridApi: GridApi,
  columnApi: ColumnApi,
  col: Column,
  node: IRowNode,
): string {
  const value = gridApi.getValue(col, node);
  const colDef = col.getColDef();
  if (colDef.valueFormatter && typeof colDef.valueFormatter === "function") {
    const formatted = colDef.valueFormatter({
      value,
      data: node.data,
      node,
      column: col,
      colDef,
      api: gridApi,
      columnApi,
      context: undefined,
    } as Parameters<typeof colDef.valueFormatter>[0]);
    return formatted != null ? String(formatted) : "";
  }
  return value != null ? String(value) : "";
}

/**
 * Build a tree mirroring the AG Grid column group hierarchy for the given
 * visible leaf columns. Each leaf column walks up via getParent() to find its
 * ancestor groups; groups are deduplicated by object identity.
 */
function buildColTree(cols: Column[], columnApi: ColumnApi): ColNode {
  const root: ColNode = { label: "", children: [] };
  const groupMap = new Map<ColumnGroup, ColNode>();

  for (const col of cols) {
    const ancestors: ColumnGroup[] = [];
    let p: ColumnGroup | null = col.getParent();
    while (p) {
      ancestors.unshift(p);
      p = p.getParent();
    }

    let node = root;
    for (const group of ancestors) {
      let child = groupMap.get(group);
      if (!child) {
        child = {
          label: group.getColGroupDef()?.headerName ?? "",
          children: [],
        };
        groupMap.set(group, child);
        node.children.push(child);
      }
      node = child;
    }

    node.children.push({
      label: columnApi.getDisplayNameForColumn(col, null) || col.getColId(),
      children: [],
    });
  }

  return root;
}

function nodeDepth(n: ColNode): number {
  return n.children.length === 0
    ? 0
    : 1 + Math.max(...n.children.map(nodeDepth));
}

function nodeLeafCount(n: ColNode): number {
  return n.children.length === 0
    ? 1
    : n.children.reduce((s, c) => s + nodeLeafCount(c), 0);
}

/**
 * Convert the column tree into pdfmake header rows.
 * Groups get colSpan = number of leaf descendants.
 * Leaf columns that start above the deepest row get rowSpan to fill down.
 */
function buildHeaderRows(cols: Column[], columnApi: ColumnApi): object[][] {
  const root = buildColTree(cols, columnApi);
  const numRows = nodeDepth(root);

  // No groups — single flat header row.
  if (numRows <= 1) {
    return [
      cols.map(col => ({
        text: columnApi.getDisplayNameForColumn(col, null) || col.getColId(),
        bold: true,
      })),
    ];
  }

  const numCols = cols.length;
  const grid: object[][] = Array.from({ length: numRows }, () =>
    Array.from({ length: numCols }, () => ({})),
  );

  function fill(node: ColNode, row: number, col: number): void {
    if (node.children.length === 0) {
      // Leaf: span remaining rows so it aligns with the deepest header row.
      const rs = numRows - row;
      const cell: Record<string, unknown> = { text: node.label, bold: true };
      if (rs > 1) cell.rowSpan = rs;
      grid[row][col] = cell;
      for (let r = row + 1; r < numRows; r++) grid[r][col] = {};
    } else {
      // Group: span all leaf-column descendants.
      const cs = nodeLeafCount(node);
      const cell: Record<string, unknown> = { text: node.label, bold: true };
      if (cs > 1) cell.colSpan = cs;
      grid[row][col] = cell;
      for (let c = 1; c < cs; c++) grid[row][col + c] = {};
      let childCol = col;
      for (const child of node.children) {
        fill(child, row + 1, childCol);
        childCol += nodeLeafCount(child);
      }
    }
  }

  let col = 0;
  for (const child of root.children) {
    fill(child, 0, col);
    col += nodeLeafCount(child);
  }

  return grid;
}

// Screen pixels are 96dpi; PDF points are 72dpi.
const PIXELS_TO_POINTS = 72 / 96;

// A4 page dimensions in points (1pt = 1/72 inch).
const A4_WIDTH_PT = 595;
const A4_HEIGHT_PT = 842;

export function exportAgGridToPdf(
  gridApi: GridApi,
  columnApi: ColumnApi,
  options: ExportAgGridToPdfOptions = {},
): void {
  const { orientation = PdfOrientation.PORTRAIT, saleTitle } = options;

  const now = new Date();
  const formattedDate = formatDateString(now);
  const printedAt = formatLocalTimestampToDateTimeString(now.getTime());

  // Only include columns that fully fit within the content area so the right
  // gutter stays clear. lightHorizontalLines adds 8pt on each side of every
  // inter-column gap → total table width = sum(widths) + 16*(N-1).
  // We account for that padding overhead incrementally as we accumulate columns.
  const H_MARGIN = 15;
  const contentWidth =
    (orientation === PdfOrientation.LANDSCAPE ? A4_HEIGHT_PT : A4_WIDTH_PT) -
    H_MARGIN * 2;
  const columns: Column[] = [];
  const widths: number[] = [];
  let totalWidth = 0;
  for (const col of columnApi.getAllDisplayedColumns()) {
    const w = parseFloat((col.getActualWidth() * PIXELS_TO_POINTS).toFixed(1));
    // Projected total: sum(widths) + 16*(N-1), where N = columns.length + 1
    if (totalWidth + w + 16 * columns.length > contentWidth) break;
    totalWidth += w;
    columns.push(col);
    widths.push(w);
  }

  const headerRows = buildHeaderRows(columns, columnApi);

  const dataRows: string[][] = [];

  gridApi.forEachNodeAfterFilterAndSort((node: IRowNode) => {
    if (node.group) {
      dataRows.push(
        columns.map((col, idx) => (idx === 0 ? String(node.key ?? "") : "")),
      );
    } else {
      dataRows.push(
        columns.map(col => getCellValue(gridApi, columnApi, col, node)),
      );
    }
  });

  const pinnedBottomRowCount = gridApi.getPinnedBottomRowCount();
  for (let i = 0; i < pinnedBottomRowCount; i++) {
    const node = gridApi.getPinnedBottomRow(i);
    if (node) {
      dataRows.push(
        columns.map(col => getCellValue(gridApi, columnApi, col, node)),
      );
    }
  }

  createAndOpenPdf(
    {
      pageOrientation: orientation,
      pageMargins: [15, 40, 15, 30],
      header: {
        stack: [
          {
            columns: [
              {
                text: saleTitle || "",
                alignment: "left",
                margin: [H_MARGIN, 12, 0, 2],
              },
              {
                text: formattedDate,
                alignment: "right",
                fontSize: 9,
                color: "#467cb5",
                margin: [0, 14, H_MARGIN, 2],
              },
            ],
          },
          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 0,
                x2: contentWidth,
                y2: 0,
                lineWidth: 1,
                lineColor: "#000000",
              },
            ],
            margin: [H_MARGIN, 0, H_MARGIN, 0],
          },
        ],
      },
      content: [
        {
          fontSize: 8,
          table: {
            headerRows: headerRows.length,
            widths,
            body: [...headerRows, ...dataRows],
          },
          layout: "lightHorizontalLines",
        },
      ],
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          {
            image: `data:image/jpeg;base64,${greyscaleLogo}`,
            fit: [60, 15],
            margin: [H_MARGIN, 5, 0, 0],
          },
          {
            text: `Printed ${printedAt} - Page ${currentPage} of ${pageCount}`,
            alignment: "right",
            margin: [0, 8, H_MARGIN, 0],
            fontSize: 8,
          },
        ],
      }),
    },
    undefined,
    true,
  );
}
